import React, { useEffect, useRef, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import './PaymentPage.css';
import HeaderBanner from '../components/HeaderBanner';
import { getUserFacingErrorMessage } from '../services/errors';

export default function PaymentPage() {
  const location = useLocation();
  const {
    message,
    purchaseId,
    sessionId: stateSessionId,
    surveyResponseId,
    clientSecret: stateClientSecret,
  } = location.state || {};

  // Support refreshes / direct navigation: try to hydrate embedded-checkout
  // credentials from localStorage when location.state is missing.
  const [hydrated] = useState(() => {
    if (stateClientSecret) {
      return { clientSecret: stateClientSecret, sessionId: stateSessionId };
    }
    try {
      const stored = localStorage.getItem('anonTempPurchase');
      if (!stored) return { clientSecret: null, sessionId: null };
      const parsed = JSON.parse(stored);
      return {
        clientSecret: parsed?.clientSecret || null,
        sessionId: parsed?.sessionId || null,
      };
    } catch {
      return { clientSecret: null, sessionId: null };
    }
  });

  const clientSecret = stateClientSecret || hydrated.clientSecret;
  const sessionId = stateSessionId || hydrated.sessionId;
  const [error, setError] = useState(null);
  const containerRef = useRef(null);
  const checkoutRef = useRef(null); // holds the embedded checkout instance

  useEffect(() => {
    let cancelled = false;
    let localInstance = null;

    async function setupEmbeddedCheckout() {
      if (!clientSecret) return;
      const STRIPE_PUBLIC_KEY = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
      if (!STRIPE_PUBLIC_KEY) {
        setError(
          'Stripe public key is missing. Please configure REACT_APP_STRIPE_PUBLIC_KEY.',
        );
        return;
      }
      try {
        // If an instance already exists (e.g., due to StrictMode), destroy it first
        if (
          checkoutRef.current &&
          typeof checkoutRef.current.destroy === 'function'
        ) {
          try {
            checkoutRef.current.destroy();
          } catch {}
          checkoutRef.current = null;
        }

        const stripe = await loadStripe(STRIPE_PUBLIC_KEY);
        if (!stripe || cancelled) return;

        localInstance = await stripe.initEmbeddedCheckout({ clientSecret });
        if (cancelled) return;

        checkoutRef.current = localInstance;
        if (containerRef.current) {
          // ensure container is empty before mounting
          containerRef.current.innerHTML = '';
          localInstance.mount(containerRef.current);
        }
      } catch (e) {
        if (cancelled) return;
        // If this came from our service layer, show safe message.
        if (e?.name === 'ApiError') {
          setError(
            getUserFacingErrorMessage(e, e.context || 'purchaseInitiate'),
          );
          return;
        }
        setError(e?.message || 'Failed to initialize embedded checkout.');
      }
    }

    setupEmbeddedCheckout();

    return () => {
      cancelled = true;
      const instance = localInstance || checkoutRef.current;
      if (instance && typeof instance.destroy === 'function') {
        try {
          instance.destroy();
        } catch {}
      }
      checkoutRef.current = null;
      if (containerRef.current) containerRef.current.innerHTML = '';
    };
  }, [clientSecret]);

  return (
    <div className='pp-root'>
      {/* <HeaderBanner title='UNLOCK YOUR REPORT' /> */}

      {/* Main */}
      <main className='pp-main'>
        <div className='pp-container'>
          {/* Left column: Stripe embedded checkout */}
          <section className='pp-column pp-left'>
            <div className='pp-box'>
              {clientSecret ? (
                <div className='pp-checkout-wrap'>
                  <div
                    ref={containerRef}
                    id='checkout'
                    className='pp-checkout-container'
                  />
                </div>
              ) : (
                <p className='pp-warning'>
                  Missing client secret. Initiate checkout from the results page
                  again.
                </p>
              )}

              {error && <p className='pp-error'>Error: {error}</p>}
            </div>
          </section>

          {/* Right column: Order summary and promotional offer */}
          <aside className='pp-column pp-right'>
            <div className='pp-box'>
              <h3 className='pp-product-title'>
                My InnerScience Personality Report
              </h3>

              {/* Content without wheel image */}
              <h4 className='pp-subtitle'>Want the Full Story?</h4>
              <p className='pp-body'>
                The complete InnerScience Personality Report is typically 30–37
                pages in length and offers a deep, personalized exploration of
                who you are—beyond labels, beyond guesswork. Here's what's
                inside:
              </p>

              <ul className='pp-checklist'>
                <li>
                  ✅ Detailed breakdown of your results across all 48
                  personality states and traits
                </li>
                <li>
                  ✅ Personalized narrative highlighting your core strengths,
                  vulnerabilities, and behavioral patterns
                </li>
                <li>
                  ✅ Insights into how your traits shift under stress—including
                  your Gray and Red Zone responses
                </li>
                <li>
                  ✅ Practical, real-life strategies for overcoming challenges
                  and building intentional growth
                </li>
              </ul>

              <p className='pp-body'>
                This full interpretive report is designed to help you recognize
                and interrupt self-sabotaging patterns, build on your most
                adaptive qualities, and grow intentionally in the areas that
                matter most— for your personal well-being, your relationships,
                and your professional life.
              </p>

              <div className='pp-total-row'>
                <span className='pp-total-label'>Total</span>
                <span className='pp-total-price'>$19.95</span>
              </div>

              {/* Placeholder for a promotional offer */}
              <div className='pp-offer'>
                <span className='pp-offer-badge'>Note</span>
                <p className='pp-offer-text'>
                  The full report will be sent to your email. <br />
                  You can download it anytime from your dashboard.
                </p>
              </div>
            </div>
          </aside>
        </div>

        {/* Footer links for small screens */}
        <div className='pp-footer-links'>
          <Link to='/'>Back to Home</Link>
        </div>
      </main>
    </div>
  );
}
